import { KeyPhase, PointerPhase, type PointerPhase as PointerPhaseValue } from './PointerState';
import { type InputManager } from './InputManager';
import { type PointerLock } from './PointerLock';

/** Pointer events the binding subscribes to, paired with the phase each one maps to. */
const POINTER_EVENTS: readonly (readonly [string, PointerPhaseValue])[] = [
  ['pointerdown', PointerPhase.Down],
  ['pointermove', PointerPhase.Move],
  ['pointerup', PointerPhase.Up],
  ['pointercancel', PointerPhase.Cancel],
  ['pointerleave', PointerPhase.Leave],
];

/** Construction options for {@link DomInputBinding}. */
export interface DomInputBindingOptions {
  /** Manager the events are pushed into. */
  readonly input: InputManager;
  /** Element pointer events are read from, normally the canvas a `CanvasSurface` owns. */
  readonly pointerTarget: HTMLElement;
  /** Where keyboard events are read from. Omit to leave the keyboard unbound. */
  readonly keyboardTarget?: EventTarget | undefined;
  /** Where the lock state is read from. Omit to always report positions. */
  readonly pointerLock?: PointerLock | undefined;
}

/**
 * Wires real DOM events into an {@link InputManager}.
 *
 * The only file in `input/` that touches the page, which is what keeps the manager itself a plain
 * state machine a test can drive with no browser. Its whole job is translation: subtract the canvas'
 * position so coordinates arrive in the canvas' own CSS pixel space, and map event names onto phases.
 *
 * Pointer Events only, so a mouse, a finger and a pen all take one path and no game ever writes a
 * touch-specific branch.
 *
 * A locked pointer is the one case where an event says something different: the browser stops reporting
 * a position and reports movement instead. The translation stays a translation, and the running total
 * that turns movement back into a position belongs to `InputManager`, where it can be tested.
 */
export class DomInputBinding {
  readonly #input: InputManager;
  readonly #pointerTarget: HTMLElement;
  readonly #keyboardTarget: EventTarget | undefined;
  readonly #pointerLock: PointerLock | undefined;

  #disposed = false;

  constructor(options: DomInputBindingOptions) {
    this.#input = options.input;
    this.#pointerTarget = options.pointerTarget;
    this.#keyboardTarget = options.keyboardTarget;
    this.#pointerLock = options.pointerLock;

    for (const [type] of POINTER_EVENTS) {
      this.#pointerTarget.addEventListener(type, this.#onPointerEvent);
    }
    this.#keyboardTarget?.addEventListener('keydown', this.#onKeyDown);
    this.#keyboardTarget?.addEventListener('keyup', this.#onKeyUp);
  }

  /** Removes every listener and drops any input the manager had queued. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;

    for (const [type] of POINTER_EVENTS) {
      this.#pointerTarget.removeEventListener(type, this.#onPointerEvent);
    }
    this.#keyboardTarget?.removeEventListener('keydown', this.#onKeyDown);
    this.#keyboardTarget?.removeEventListener('keyup', this.#onKeyUp);
    this.#input.reset();
  }

  readonly #onPointerEvent = (event: Event): void => {
    const pointerEvent = event as PointerEvent;
    const phase = POINTER_EVENTS.find(([type]) => type === event.type)?.[1];
    if (phase === undefined) {
      return;
    }

    /*
     * A locked press still carries the coordinates the cursor was frozen at when the lock began, which
     * are nothing to do with where the player is pointing. Sending movement for every phase rather than
     * only for moves is what keeps those coordinates out of the engine entirely.
     */
    if (this.#pointerLock?.isLocked === true) {
      this.#input.submitPointer({
        pointerId: pointerEvent.pointerId,
        phase,
        dx: pointerEvent.movementX,
        dy: pointerEvent.movementY,
      });
      return;
    }

    const bounds = this.#pointerTarget.getBoundingClientRect();
    this.#input.submitPointer({
      pointerId: pointerEvent.pointerId,
      phase,
      x: pointerEvent.clientX - bounds.left,
      y: pointerEvent.clientY - bounds.top,
    });
  };

  readonly #onKeyDown = (event: Event): void => {
    this.#input.submitKey({ code: (event as KeyboardEvent).code, phase: KeyPhase.Down });
  };

  readonly #onKeyUp = (event: Event): void => {
    this.#input.submitKey({ code: (event as KeyboardEvent).code, phase: KeyPhase.Up });
  };
}
